import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { usePlatformApi } from "../../hooks/usePlatformApi";
import type { FeedbackStatsGroupBy } from "../../types/feedback";

interface Props {
  /** Subset of the browser filters that affect stats (excludes paging + free-text). */
  filterSlice: { orgId?: string; fromDate?: string; toDate?: string };
}

const GROUPINGS: Array<{ value: FeedbackStatsGroupBy; label: string }> = [
  { value: "surface", label: "Surface" },
  { value: "polarity", label: "Polarity" },
  { value: "category", label: "AI Category" },
  { value: "orgId", label: "Org" },
];

/**
 * Top-line dashboard fed by `GET /api/platform/feedback/stats`. The
 * groupBy toggle drives a fresh fetch; the date / orgId filter slice is
 * forwarded so charts respect the same window the table is showing.
 */
export function FeedbackCharts({ filterSlice }: Props) {
  const api = usePlatformApi();
  const [groupBy, setGroupBy] = useState<FeedbackStatsGroupBy>("surface");
  const [buckets, setBuckets] = useState<Array<{ key: string; count: number }>>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .feedbackStats({ groupBy, ...filterSlice })
      .then((res) => {
        if (!cancelled) setBuckets(res.data.buckets);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : "Failed to load stats");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [api, groupBy, filterSlice.orgId, filterSlice.fromDate, filterSlice.toDate]);

  return (
    <div className="rounded-md border border-slate-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-900">
      <div className="mb-3 flex items-center gap-3">
        <h3 className="text-sm font-semibold">Feedback distribution</h3>
        <div className="flex gap-1">
          {GROUPINGS.map((g) => (
            <button
              key={g.value}
              type="button"
              onClick={() => setGroupBy(g.value)}
              className={`rounded-md px-2 py-1 text-xs font-medium transition-colors ${
                groupBy === g.value
                  ? "bg-indigo-100 text-indigo-700 dark:bg-indigo-950 dark:text-indigo-300"
                  : "text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800"
              }`}
            >
              {g.label}
            </button>
          ))}
        </div>
        {loading && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
      </div>

      {error && (
        <div className="rounded-md bg-red-50 p-2 text-xs text-red-700 dark:bg-red-950 dark:text-red-400">
          {error}
        </div>
      )}

      {!loading && !error && buckets.length === 0 && (
        <div className="py-8 text-center text-sm text-slate-400">No data in this window.</div>
      )}

      {buckets.length > 0 && (
        <div
          className="h-48 w-full"
          role="img"
          aria-label={`Feedback counts grouped by ${groupBy}, ${buckets.length} bucket${buckets.length === 1 ? "" : "s"}`}
        >
          <ResponsiveContainer>
            <BarChart data={buckets} layout="vertical" margin={{ top: 4, right: 16, left: 0, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgb(148 163 184 / 0.3)" />
              <XAxis type="number" allowDecimals={false} stroke="currentColor" fontSize={11} />
              <YAxis type="category" dataKey="key" width={140} stroke="currentColor" fontSize={11} />
              <Tooltip
                contentStyle={{ fontSize: 12 }}
                formatter={(value: number) => [value, "count"]}
              />
              <Bar dataKey="count" fill="#6366f1" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
